import React, { useState, useEffect } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, KeyboardSensor } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '../../../../services/apiServices'
// Shadcn/ui and lucide-react imports
import { Button } from '../../../../components/ui/button'; // Assuming this path is correct for your project
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../../components/ui/card'; // [cite: 15]
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select'; // [cite: 23]
import { Badge } from '../../../../components/ui/badge'; // [cite: 18]
import { Switch } from '../../../../components/ui/switch';
import { Label } from '../../../../components/ui/label';
import { useToast } from '../../../../hooks/use-toast'; // Assuming this path is correct
import { PlusCircle, Edit2, X, Settings2, Trash2, Undo2, RotateCcw, Save, Download, GripVertical, Info, Copy } from 'lucide-react'; // Removed AlertTriangle as it wasn't used

import { QUESTION_TYPES } from './questionTypes';
import QuestionForm from './QuestionForm';
import ConditionModal from './ConditionModal';
import { useAllCategoryData } from '../../../../hooks/UseCategoryData';
import { CategorySearch } from '../CategorySearch';
import { useAdminStore } from '../../../../store/adminStore'; // <-- NEW IMPORT

const sortQuestionOptions = (question) => {
  if (!question || !Array.isArray(question.options)) {
    return question;
  }

  const sortedOptions = [...question.options]
    .sort((a, b) => a.order_index - b.order_index);

  return { ...question, options: sortedOptions };
};

export default function EditSubTab({ questions, setQuestions, categoryId, fetchQuestions, showInactive, onToggleShowInactive }) {
  const [formOpenIds, setFormOpenIds] = useState([]);
  // State for managing the ConditionModal open state and the question it targets
  const [isLogicModalOpen, setIsLogicModalOpen] = useState(false);
  const [currentQuestionForLogic, setCurrentQuestionForLogic] = useState(null);

  const [importSourceType, setImportSourceType] = useState(null); // 'master', 'sameLocal', 'otherLocal'
  const [importOtherLocale, setImportOtherLocale] = useState(null); // 'IN', 'US', etc.
  const [importSearchContext, setImportSearchContext] = useState(null); // The context to pass to CategorySearch
  const [importFetchContext, setImportFetchContext] = useState(null); // The context to use for fetching questions

  //const { categories } = useAllCategoryData();
  const [importCategoryId, setImportCategoryId] = useState(null);
  const [importQuestions, setImportQuestions] = useState([]);
  const [selectedImportQuestionId, setSelectedImportQuestionId] = useState(null);
  const [importCategory, setImportCategory] = useState(null);
  const { toast } = useToast();
  const locale = useAdminStore((state) => state.locale); // <-- GET CONTEXT
  const allCountries = useAdminStore((state) => state.countries);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    setImportCategoryId(null);
    setImportCategory(null);
    setImportQuestions([]);
    setSelectedImportQuestionId(null);
    setImportOtherLocale(null);
    setImportSearchContext(null);
    setImportFetchContext(null);

    if (importSourceType === 'master') {
      setImportSearchContext('master');
      setImportFetchContext('master');
    } else if (importSourceType === 'sameLocal') {
      setImportSearchContext(locale);
      setImportFetchContext(locale);
    }
  }, [importSourceType, locale]);

  useEffect(() => {
    const fetchImportQuestions = async () => {
      // Don't fetch unless we have a category AND a context to fetch from
      if (!importCategoryId || !importFetchContext) {
        setImportQuestions([]);
        setSelectedImportQuestionId(null);
        return;
      }

      try {
        const response = await api.get(`/admin/categories/cat-qna/category/${importCategoryId}`, {
          params: { status: "draft", locale: importFetchContext }, // Use the correct fetch context
        });
        setImportQuestions(response.data);
      } catch (err) {
        console.error("Failed to fetch import questions", err);
        toast({
          variant: "destructive",
          title: "Import Error",
          description: err?.response?.data?.message || err.message || "Could not load questions.",
        });
        setImportQuestions([]);
      }
    };

    fetchImportQuestions();
  }, [importCategoryId, importFetchContext, toast]); // Depends on importFetchContext

  const handleImport = async () => {
    if (!selectedImportQuestionId || !importFetchContext) {
      toast({
        variant: "destructive",
        title: "Import Failed",
        description: "No question or fetch context selected.",
      });
      return;
    }

    try {
      // --- Fetch the question from the correct context ---
      const res = await api.get(`/admin/categories/cat-qna/${selectedImportQuestionId}`, {
        params: { locale: importFetchContext }
      });
      
      const raw_data = res.data;
      const data = sortQuestionOptions(raw_data);
      
      const newQuestion = {
        id: `temp-${Date.now()}`,
        label: data.label,
        value: data.value,
        question_type: data.question_type,
        is_required: data.is_required,
        seller_visible: data.seller_visible,
        conditional_logic: data.conditional_logic || {},
        conditional: data.conditional || false,
        options: data.options || [],
        order_index: questions.length,
        config: data.config || {},
        images: data.images || [],
        isNew: true,
        isEdited: false,
      };

      setQuestions(prevQuestions => [...prevQuestions, newQuestion]);
      
      toast({
        title: "Question Imported",
        description: `"${data.label}" added to the draft. Remember to save all changes.`,
        variant: "default",
      });
      setSelectedImportQuestionId(null);
    } catch (err) {
      console.error('Error during import process:', err);
      toast({
        variant: "destructive",
        title: "Import Error",
        description: err.message || "An unexpected error occurred during import.",
      });
    }
  };

  const canBeConditional = (type) => {
    const found = QUESTION_TYPES.find(q => q.value === type);
    return found?.canBeConditional;
  };

  const canTriggerConditional = (type) => {
    const found = QUESTION_TYPES.find(q => q.value === type);
    return found?.canTriggerConditional;
  };

  const handleToggleExpand = (id) => {
    setFormOpenIds(prevIds =>
      prevIds.includes(id) ? prevIds.filter(openId => openId !== id) : [...prevIds, id]
    );
    // If a form is opened, ensure the logic modal for that question is closed.
    if (currentQuestionForLogic?.id === id && !formOpenIds.includes(id)) {
        setIsLogicModalOpen(false);
        setCurrentQuestionForLogic(null);
    }
  };

  const handleUpdateQuestion = (id, updatedForm) => {
    setQuestions(prev =>
      prev.map(q => (q.id === id ? { ...q, ...updatedForm, isEdited: !q.isNew } : q))
    );
  };
  
  const handleQuestionFormSave = (id, updatedData) => {
    handleUpdateQuestion(id, updatedData);
    setFormOpenIds(prev => prev.filter(openId => openId !== id));
     toast({
        title: "Question Updated",
        description: `"${updatedData.label}" has been updated in the draft.`,
    });
  };

  const handleAddNew = () => {
    const newQuestionId = `temp-${Date.now()}`;
    const newQuestion = {
      id: newQuestionId,
      label: '',
      value: '',
      question_type: 'text',
      is_required: true,
      seller_visible: true,
      conditional_logic: {},
      conditional: false, // Default conditional to false
      options: [],
      order_index: questions.length,
      images: [],
      config: {},
      isNew: true,
      isEdited: false,
    };
    setQuestions(prev => [...prev, newQuestion]);
    setFormOpenIds(prev => [...prev, newQuestionId]);
  };

  // Turns a Common question into a Control/Test pair: the original stays in
  // place tagged 'control', a clone is inserted right after it tagged 'test'.
  // Both share a transient ab_pair_id (client-side only, never sent to the
  // backend) so handleSaveAll can give them the same order_index — that's what
  // makes them occupy the same slot in their respective filtered variant lists.
  const handleDuplicateAsTestVariant = (question) => {
    const pairId = `pair-${Date.now()}`;
    const clonedValue = `${question.value || 'question'}_test_${Date.now().toString(36)}`;

    setQuestions(prev => {
      const idx = prev.findIndex(q => q.id === question.id);
      if (idx === -1) return prev;

      const updatedOriginal = {
        ...prev[idx],
        ab_variant: 'control',
        ab_pair_id: pairId,
        isEdited: !prev[idx].isNew,
      };

      const clone = {
        ...prev[idx],
        id: `temp-${Date.now()}`,
        value: clonedValue,
        ab_variant: 'test',
        ab_pair_id: pairId,
        isNew: true,
        isEdited: false,
        order_index_changed: undefined,
      };

      const next = [...prev];
      next[idx] = updatedOriginal;
      next.splice(idx + 1, 0, clone);
      return next;
    });

    toast({
      title: "Test Variant Created",
      description: `"${question.label}" tagged Control; a Test copy was added below it. Edit the copy, then Save All + Publish.`,
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!active || !over || active.id === over.id) return;
    
    setQuestions(prevQuestions => {
      const oldIndex = prevQuestions.findIndex(q => q.id === active.id);
      const newIndex = prevQuestions.findIndex(q => q.id === over.id);
      const movedQuestions = arrayMove(prevQuestions, oldIndex, newIndex);
      return movedQuestions.map(q => ({ ...q, isEdited: !q.isNew ? true : q.isEdited, order_index_changed: true }));
    });
  };

  const handleDeleteQuestion = (id) => {
    setQuestions(prev =>
      prev.map(q => (q.id === id ? { ...q, is_deleted: true, isEdited: !q.isNew } : q))
    );
    toast({
        title: "Question Marked for Deletion",
        description: "The question will be permanently deleted when you save all changes.",
        variant: "default"
    });
  };

  const handleUndoDelete = (id) => {
    setQuestions(prev =>
      prev.map(q => (q.id === id ? { ...q, is_deleted: false, isEdited: !q.isNew } : q))
    );
  };

  // A question that arrived from the server already archived/deleted (only
  // possible when "Show inactive" is on) and hasn't been marked for
  // reactivation yet.
  const isServerInactive = (q) => !q.is_deleted && !q.pending_reactivate && q.status && q.status !== 'draft';

  const handleReactivateQuestion = (id) => {
    setQuestions(prev =>
      prev.map(q => (q.id === id ? { ...q, pending_reactivate: true, isEdited: !q.isNew } : q))
    );
    toast({
      title: "Question Marked for Reactivation",
      description: "It will be reactivated when you save all changes.",
      variant: "default"
    });
  };

  const handleUndoReactivate = (id) => {
    setQuestions(prev =>
      prev.map(q => (q.id === id ? { ...q, pending_reactivate: false, isEdited: !q.isNew } : q))
    );
  };

  const handleSaveAll = async () => {
    let success = true;
    let operationsCount = 0;
    let errors = [];

    const questionsToProcess = questions.filter(
      (q) => q.isNew || q.isEdited || q.is_deleted || q.order_index_changed
    );

    if (questionsToProcess.length === 0) {
      toast({ title: "No Changes", description: "There are no changes to save." });
      return;
    }

    try {
      // --- DELETE ---
      const deletePromises = questions
        .filter((q) => q.is_deleted && !q.isNew)
        .map(async (q) => {
          operationsCount++;
          try {
            await api.delete(`/admin/categories/cat-qna/${q.id}`,
              {
                params: { locale: locale},
              }
            );
          } catch (err) {
            const message = err?.response?.data?.message || `Failed to delete "${q.label || q.id}"`;
            errors.push(message);
            success = false;
          }
        });

      await Promise.all(deletePromises);

      // --- CREATE / UPDATE ---
      const nonDeletedQuestions = questions.filter((q) => !q.is_deleted);

      // Control/Test pairs (from "Duplicate as Test Variant") must share the same
      // order_index so each lands in the same slot within its own filtered
      // (Control-only / Test-only) list at publish time — order_index is the only
      // thing that determines position within a variant's list, there's no
      // separate pairing id sent to the backend.
      const pairOrderIndex = {};
      nonDeletedQuestions.forEach((q, idx) => {
        if (q.ab_pair_id && !(q.ab_pair_id in pairOrderIndex)) {
          pairOrderIndex[q.ab_pair_id] = idx;
        }
      });
      const resolveOrderIndex = (q, idx) => (q.ab_pair_id ? pairOrderIndex[q.ab_pair_id] : idx);

      // Defense-in-depth: this pairing rule should make order_index collisions
      // within a single variant's list impossible by construction, but it's new,
      // first-version logic — verify before sending anything rather than trusting
      // it silently. The backend re-checks this at publish time too.
      const findVariantCollisions = (excludedVariant) => {
        const seen = new Map();
        const collisions = [];
        nonDeletedQuestions.forEach((q, idx) => {
          if (q.ab_variant === excludedVariant) return;
          const orderIdx = resolveOrderIndex(q, idx);
          if (seen.has(orderIdx)) {
            collisions.push([seen.get(orderIdx), q.label || q.id]);
          } else {
            seen.set(orderIdx, q.label || q.id);
          }
        });
        return collisions;
      };
      const controlCollisions = findVariantCollisions('test');
      const testCollisions = findVariantCollisions('control');
      if (controlCollisions.length || testCollisions.length) {
        toast({
          variant: "destructive",
          title: "Cannot Save: Ordering Conflict",
          description: "Two questions in the same A/B variant would share a position. Please reorder before saving.",
        });
        return;
      }

      const savePromises = nonDeletedQuestions
        .map(async (q, idx) => {
          if (q.isNew || q.isEdited || q.order_index_changed) {
            operationsCount++;

            const correctlyOrderedOptions = (q.options || [])
            .map((opt, optionIndex) => ({
              ...opt,
              // Ensure temp IDs are stripped for the backend
              id: Number.isInteger(opt.id) ? opt.id : null,
              // Re-assign a sequential order_index
              order_index: optionIndex,
            }));
            const payload = {
              label: q.label,
              value: q.value,
              question_type: q.question_type,
              is_required: q.is_required,
              seller_visible: q.seller_visible,
              conditional: q.conditional,
              conditional_logic: q.conditional_logic,
              options: correctlyOrderedOptions,
              images: q.images,
              order_index: resolveOrderIndex(q, idx),
              category_id: categoryId,
              config: q.config || {},
              nudge_text: q.nudge_text || "",
              ab_variant: q.ab_variant || null,
              ...(q.pending_reactivate ? { status: "draft" } : {}),
            };

            try {
              if (q.isNew) {
                await api.post("/admin/categories/cat-qna/create", payload,
                  {params: { locale },}
                );
              } else {
                await api.put(`/admin/categories/cat-qna/${q.id}`, payload,
                  { params: { locale },}
                );
              }
            } catch (err) {
              const message = err?.response?.data?.message || `Failed to save "${q.label || q.id}"`;
              errors.push(message);
              success = false;
            }
          }
        });

      await Promise.all(savePromises);

      // --- TOAST ---
      if (success && errors.length === 0) {
        toast({
          title: "Save Successful!",
          description: `All ${operationsCount > 0 ? operationsCount : "relevant"} changes to questions have been saved.`,
          variant: "default",
        });
      } else {
        toast({
          variant: "destructive",
          title: `Save Partially Failed (${operationsCount} operations attempted)`,
          description: (
            <div>
              <p>Some questions could not be saved. Please review and try again.</p>
              <ul className="mt-2 list-disc list-inside text-xs">
                {errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          ),
        });
      }

      fetchQuestions(); // refresh data either way
    } catch (error) {
      console.error("Error saving all questions:", error);
      toast({
        variant: "destructive",
        title: "Save Failed",
        description: error.message || "An unexpected error occurred while saving questions.",
      });
    }
  };

  const getTypeLabel = (type) => {
    const match = QUESTION_TYPES.find(q => q.value === type);
    return match ? match.label : type;
  };

  // For ConditionModal
  const openLogicModal = (question) => {
    // Close edit form if open for this question
    if (formOpenIds.includes(question.id)) {
        handleToggleExpand(question.id); 
    }
    setCurrentQuestionForLogic(question);
    setIsLogicModalOpen(true);
  };

  const handleSaveLogic = ({ logic, isEnabled }) => {
    if (currentQuestionForLogic) {
      handleUpdateQuestion(currentQuestionForLogic.id, {
        conditional_logic: logic,
        isEdited: !currentQuestionForLogic.isNew,
      });
      toast({ title: "Conditional Logic Updated", description: `Logic for "${currentQuestionForLogic.label}" saved.` });
    }
    setIsLogicModalOpen(false);
    setCurrentQuestionForLogic(null);
  };


  function SortableRow({ id, question, children }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
    const style = { transform: CSS.Transform.toString(transform), transition };

    let rowClass = "border-b border-border group"; // Added group for potential group-hover states
    if (question.is_deleted) {
      rowClass += " opacity-50 bg-muted/30";
    } else if (question.pending_reactivate) {
      rowClass += " bg-green-50/50";
    } else if (isServerInactive(question)) {
      rowClass += " opacity-50 bg-muted/20";
    } else if (question.conditional) {
      rowClass += " bg-accent/20";
    }
    if (formOpenIds.includes(id) || currentQuestionForLogic?.id === id) { // Check against currentQuestionForLogic
        rowClass += " bg-muted/50";
    }

    return (
      <tr ref={setNodeRef} style={style} className={rowClass}>
        {React.Children.map(children, (child, index) => {
          if (index === 0 && child.props.className?.includes('drag-handle')) {
            return React.cloneElement(child, {
              ...attributes,
              ...listeners,
            });
          }
          return child;
        })}
      </tr>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <Button onClick={handleAddNew} variant="default">
          <PlusCircle className="mr-2 h-4 w-4" /> Add New Question
        </Button>
        <div className="flex items-center space-x-2">
          <Switch
            id="show-inactive-qna"
            checked={!!showInactive}
            onCheckedChange={onToggleShowInactive}
          />
          <Label htmlFor="show-inactive-qna" className="font-normal cursor-pointer text-sm text-muted-foreground">
            Show inactive / deleted
          </Label>
        </div>
      </div>

      {/* --- NEW IMPORT CARD --- */}
      <Card>
        <CardHeader>
          <CardTitle>Import Existing Question</CardTitle>
          <CardDescription>Add questions from other categories to speed up setup.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          
          {/* Step 1: Select Source */}
          <div>
            <Select value={importSourceType || ""} onValueChange={setImportSourceType}>
              <SelectTrigger>
                <SelectValue placeholder="Step 1: Select Import Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="master">From Master</SelectItem>
                <SelectItem value="sameLocal">From Same Local ({locale})</SelectItem>
                <SelectItem value="otherLocal">From Other Local</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Step 2a: Select "Other Local" Country */}
          {importSourceType === 'otherLocal' && (
            <div>
              <Select value={importOtherLocale || ""} onValueChange={setImportOtherLocale}>
                <SelectTrigger>
                  <SelectValue placeholder="Step 2: Select Country" />
                </SelectTrigger>
                <SelectContent>
                  {allCountries
                    .filter(c => c.country_code !== locale) // Filter out current locale
                    .map(c => (
                      <SelectItem key={c.country_code} value={c.country_code}>
                        {c.name} ({c.country_code})
                      </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Step 2b: Search for Category */}
          {importSearchContext && (
            <div>
              <CategorySearch
                key={importSearchContext} // Add key to force re-render when context changes
                locale={importSearchContext} // Pass the correct context
                onSelect={(cat) => {
                  setImportCategory(cat);
                  setImportCategoryId(cat?.id);
                  setSelectedImportQuestionId(null); // Reset question on new category
                }}
              />
              {importCategory && (
                <div className="text-sm text-muted-foreground mt-1">
                  Selected Category: <span className="font-medium text-foreground">{importCategory.name}</span>
                </div>
              )}
            </div>
          )}
          
          {/* Step 3: Select Question and Import */}
          {importCategoryId && (
            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              <div className="flex-grow">
                  <Select
                      value={selectedImportQuestionId ? String(selectedImportQuestionId) : ''}
                      onValueChange={(value) => setSelectedImportQuestionId(value ? Number(value) : null)}
                      disabled={importQuestions.length === 0}
                  >
                      <SelectTrigger>
                      <SelectValue placeholder="Step 3: Select question to import" />
                      </SelectTrigger>
                      <SelectContent>
                      {importQuestions.length === 0 ? (
                          <SelectItem value="no-questions" disabled>No draft questions in selected category.</SelectItem>
                      ) : (
                        importQuestions.map(q => (
                            <SelectItem key={q.id} value={String(q.id)}>
                            {q.label} ({getTypeLabel(q.question_type)})
                            </SelectItem>
                        ))
                      )}
                      </SelectContent>
                  </Select>
              </div>
              <Button
                onClick={handleImport}
                disabled={!selectedImportQuestionId}
                variant="secondary"
              >
                <Download className="mr-2 h-4 w-4" /> Import Question
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      {/* --- END NEW IMPORT CARD --- */}

      {questions.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">
            <Info className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium">No Questions Yet</h3>
            <p className="mt-1">Click "Add New Question" to get started or import from another category.</p>
        </div>
      ) : (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="overflow-x-auto">
        <table className="min-w-full border-collapse border border-border text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-2 py-3 text-left font-medium text-muted-foreground border-b border-border w-10"></th>
              <th className="px-2 py-3 text-left font-medium text-muted-foreground border-b border-border w-12">#</th>
              <th className="px-3 py-3 text-left font-medium text-muted-foreground border-b border-border w-2/5">Label</th> {/* Increased width */}
              <th className="px-3 py-3 text-left font-medium text-muted-foreground border-b border-border w-1/5">Type</th> {/* Adjusted width */}
              <th className="px-3 py-3 text-left font-medium text-muted-foreground border-b border-border">Required</th>
              <th className="px-3 py-3 text-left font-medium text-muted-foreground border-b border-border">Seller Visible</th>
              <th className="px-3 py-3 text-left font-medium text-muted-foreground border-b border-border w-1/6">Status</th> {/* Adjusted width */}
              <th className="px-3 py-3 text-left font-medium text-muted-foreground border-b border-border">Variant</th>
              <th className="px-3 py-3 text-center font-medium text-muted-foreground border-b border-border w-auto min-w-[150px]">Actions</th> {/* Ensured min width */}
            </tr>
          </thead>
          <SortableContext items={questions.map(q => q.id)} strategy={verticalListSortingStrategy}>
            <tbody>
              {questions.map((q, idx) => (
                <React.Fragment key={q.id}>
                  <SortableRow id={q.id} question={q}>
                    <td className="px-2 py-2 drag-handle cursor-move text-muted-foreground hover:text-foreground flex justify-self-auto items-center h-full">
                        <GripVertical className="h-5 w-5" />
                    </td>
                    <td className="px-2 py-2 border-b border-border text-muted-foreground">{idx + 1}</td>
                    <td className={`px-3 py-2 border-b border-border font-medium ${q.is_deleted ? 'line-through' : 'text-foreground'}`}>
                        {q.label || <span className="italic text-muted-foreground">No label</span>}
                        {q.conditional && <Badge variant="outline" className="ml-2 text-xs border-orange-400 text-orange-600">?</Badge>}
                    </td>
                    <td className="px-3 py-2 border-b border-border text-muted-foreground">{getTypeLabel(q.question_type)}</td>
                    <td className="px-3 py-2 border-b border-border text-muted-foreground">{q.is_required ? 'Yes' : 'No'}</td>
                    <td className="px-3 py-2 border-b border-border text-muted-foreground">{q.seller_visible ? 'Yes' : 'No'}</td>
                    <td className="px-3 py-2 border-b border-border">
                      {q.is_deleted ? (
                        <Badge variant="destructive" className="text-xs">Marked for Deletion</Badge>
                      ) : q.pending_reactivate ? (
                        <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 border-green-300">Pending Reactivation</Badge>
                      ) : isServerInactive(q) ? (
                        <Badge variant="secondary" className="text-xs">
                          {q.status === 'archived' ? 'Archived' : q.status === 'published' ? 'Published (not live)' : q.status === 'deleted' ? 'Deleted' : q.status}
                        </Badge>
                      ) : q.isNew ? (
                        <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 border-blue-300">New</Badge>
                      ) : q.isEdited ? (
                        <Badge variant="outline" className="text-xs border-yellow-400 text-yellow-600">Edited</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Saved</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 border-b border-border">
                      {q.ab_variant === 'control' ? (
                        <Badge variant="outline" className="text-xs border-blue-400 text-blue-600">Control</Badge>
                      ) : q.ab_variant === 'test' ? (
                        <Badge variant="outline" className="text-xs border-purple-400 text-purple-600">Test</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">Common</span>
                      )}
                    </td>
                    <td className="px-3 py-2 border-b border-border">
                      <div className="flex items-center justify-center gap-0.5"> {/* Reduced gap */}
                        {isServerInactive(q) ? (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-green-600 hover:text-green-700"
                            onClick={() => handleReactivateQuestion(q.id)}
                            title="Reactivate Question"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        ) : q.pending_reactivate ? (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-green-600 hover:text-green-700"
                            onClick={() => handleUndoReactivate(q.id)}
                            title="Undo Reactivate"
                          >
                            <Undo2 className="h-4 w-4" />
                          </Button>
                        ) : (
                        <>
                        <Button
                          variant="ghost" // [cite: 12]
                          size="icon-sm" // Custom size or use 'sm' and padding adjustments
                          onClick={() => handleToggleExpand(q.id)}
                          disabled={q.is_deleted}
                          title={formOpenIds.includes(q.id) ? 'Close Edit' : 'Edit Question'}
                        >
                          {formOpenIds.includes(q.id) ? <X className="h-4 w-4" /> : <Edit2 className="h-4 w-4" />}
                        </Button>
                        {canTriggerConditional(q.question_type) && !formOpenIds.includes(q.id) && !q.is_deleted && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => openLogicModal(q)}
                            title="Setup Logic"
                            className='text-muted-foreground hover:text-foreground ml-4'
                          >
                            <Settings2 className="h-4 w-4" />
                          </Button>
                        )}
                        {!q.ab_variant && !q.is_deleted && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => handleDuplicateAsTestVariant(q)}
                            title="Duplicate as Test Variant"
                            className='text-muted-foreground hover:text-foreground ml-4'
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        )}
                        {!q.is_deleted ? (
                          <Button
                            variant="ghost" // [cite: 12]
                            size="icon-sm"
                            className="text-destructive hover:text-destructive ml-4"
                            onClick={() => handleDeleteQuestion(q.id)}
                            title="Delete Question"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-green-600 hover:text-green-700" // Consider a success color from your theme if available
                            onClick={() => handleUndoDelete(q.id)}
                            title="Undo Delete"
                          >
                            <Undo2 className="h-4 w-4" />
                          </Button>
                        )}
                        </>
                        )}
                      </div>
                    </td>
                  </SortableRow>
                  {formOpenIds.includes(q.id) && !q.is_deleted && (
                    <tr>
                      <td colSpan={9} className="p-0">
                        <div className="p-4 bg-muted/20 border-b border-border"> {/* Slightly less opaque background */}
                          <QuestionForm
                            mode={q.isNew ? 'create' : 'edit'}
                            initialData={q}
                            onSave={(updatedData) => handleQuestionFormSave(q.id, updatedData)}
                            onClose={() => handleToggleExpand(q.id)}
                            onAddToDraftList={(updatedData) => handleQuestionFormSave(q.id, updatedData)}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                  {/* ConditionModal is now rendered outside the map, controlled by isLogicModalOpen */}
                </React.Fragment>
              ))}
            </tbody>
          </SortableContext>
        </table>
        </div>
      </DndContext>
      )}

      {/* Render ConditionModal outside the loop, controlled by state */}
      {isLogicModalOpen && currentQuestionForLogic && (
        <ConditionModal
          triggerQuestion={currentQuestionForLogic}
          allQuestions={questions.filter(qn => !qn.is_deleted && !isServerInactive(qn) && qn.id !== currentQuestionForLogic.id)}
          onClose={() => {
            setIsLogicModalOpen(false);
            setCurrentQuestionForLogic(null);
          }}
          onSave={handleSaveLogic}
          canBeConditional={canBeConditional}
          locale={locale}
        />
      )}

      {questions.length > 0 && (
        <div className="flex justify-end mt-6">
          <Button
            onClick={handleSaveAll}
            variant="default" // [cite: 10]
            size="lg" // [cite: 13]
            disabled={!questions.some(q => q.isNew || q.isEdited || q.is_deleted || q.order_index_changed)}
          >
            <Save className="mr-2 h-5 w-5" /> Save All Changes
          </Button>
        </div>
      )}
    </div>
  );
}